/**
 * NOTES, your research notebook, organized into blocks.
 *
 * URL structure (handled in App.jsx):
 *   /projects                         → grid of all blocks
 *   /projects/:block                  → all notes inside a block  (e.g. /projects/radarconf26)
 *   /projects/:block/:note            → a single note
 *   /projects/tag/:tag                → every note with a #tag, across all blocks
 *
 * ─────────────────────────────────────────────────────────────────────────
 * HOW TO ADD A NOTE
 *   1. Pick (or add) a block below.
 *   2. Add an object to `notes` with a unique `slug`, the block's slug, a
 *      `title`, `summary`, some `tags`, optional `images` / `links`, and a
 *      `body` written in markdown-lite (see MarkdownLite.jsx for syntax).
 *   3. Set `isNew: true` to show a "new" badge for a while. Add `added: "YYYY-MM-DD"`
 *      to sort it to the front of "Recently added". That's it.
 *
 * TAGS are single tokens (no spaces) so they read as #hashtags. The same tag
 * used on notes in different blocks is what cross-references them, clicking
 * any #tag opens a page listing every note that shares it.
 * ─────────────────────────────────────────────────────────────────────────
 */

/** Array order is display order on the Projects page (3-up grid on large screens). */
export const blocks = [
  {
    // Slug kept as satellite-comms so existing /projects/satellite-comms/* URLs stay valid.
    slug: "satellite-comms",
    title: "5G & NTN",
    blurb:
      "5G NR and NB-IoT over non-terrestrial networks, testbeds, standards work, voice, data, and link design over GEO/LEO satellites.",
  },
  {
    slug: "whales",
    title: "Whales & Marine Sensing",
    blurb:
      "CATS bio-logging tags: GNSS positioning, dead reckoning, and 3-D sensor fusion for marine-mammal tracking.",
  },
  {
    slug: "leo-pnt",
    title: "LEO PNT",
    blurb:
      "Positioning, navigation & timing from low-Earth-orbit constellations, GNSS-alternative PNT.",
  },
  {
    slug: "underwater-comms",
    title: "Underwater Comms",
    blurb:
      "Acoustic, magnetic-induction, and semantic links for autonomous underwater systems.",
  },
  {
    slug: "radarconf26",
    title: "RadarConf '26",
    blurb:
      "Phased-array & radar sensing built on real X-band hardware, the RBC Labs bootcamp notebooks.",
  },
  {
    slug: "gnss",
    title: "GNSS & Positioning",
    blurb:
      "Positioning, navigation & timing, GNSS receivers, fast sync, and inertial fusion.",
  },
];

export const notes = [
  // ───────────── RadarConf '26 ─────────────
  {
    slug: "bootcamp-overview",
    block: "radarconf26",
    title: "RBC Labs Phased Array & Sensing Bootcamp",
    summary:
      "Two-day hands-on bootcamp (Analog Devices × MathWorks, May 2026) pairing phased-array theory with live MATLAB labs on real X-band hardware.",
    isNew: true,
    tags: ["PhasedArrays", "Radar", "FMCW", "Beamforming", "MATLAB", "SDR"],
    images: [
      "/images/projects/radar-overview/fig-1.png",
      "/images/projects/radar-overview/fig-2.png",
    ],
    links: {
      notion:
        "https://app.notion.com/p/Bootcamp-Overview-37f8349b0e168063a990cf5410d10603?source=copy_link",
    },
    body: `A two-day hands-on bootcamp (Analog Devices × MathWorks, May 2026) pairing phased-array theory with live MATLAB labs on real **X-band hardware**, the ADALM-PHASER board, PlutoSDR, and HB100 source.

It covers the full sensing stack from the radar range equation through #FMCW, stretch processing, #Beamforming, and adaptive nulling, going from zero to prototyping your own phased-array sensing system.

The material is split into two companion notebooks:

- **Book 1, Sensing Fundamentals**: range, FMCW, Doppler & detection.
- **Book 2, Beamforming & Phased Arrays**: steering, nulling & tracking.

Related: #PhasedArrays #Radar #SDR #MATLAB`,
  },
  {
    slug: "sensing-fundamentals",
    block: "radarconf26",
    title: "Sensing Fundamentals: Range, FMCW, Doppler & Detection",
    summary:
      "Book 1 of the bootcamp: the radar sensing pipeline from first principles, each concept paired with a live lab on the ADALM-PHASER.",
    isNew: true,
    tags: ["FMCW", "RangeDoppler", "StretchProcessing", "MTI", "CFAR", "Radar"],
    images: [
      "/images/projects/radar-sensing/fig-1.png",
      "/images/projects/radar-sensing/fig-2.png",
    ],
    links: {
      notion:
        "https://app.notion.com/p/Sensing-Fundamentals-37f8349b0e1680cbad53c68a1e10cb41?source=copy_link",
    },
    body: `Book 1 of the RBC Labs bootcamp walks the radar sensing pipeline from first principles.

It covers the range equation and link budget; #FMCW chirps and **stretch processing** (turning 500 MHz of bandwidth into a few-kHz beat tone a \$250 SDR can capture); 2D #RangeDoppler maps; #MTI clutter cancellation; and #CFAR adaptive detection.

Every concept is paired with a live lab on the ADALM-PHASER.

Continues in #Beamforming · part of #Radar`,
  },
  {
    slug: "beamforming-phased-arrays",
    block: "radarconf26",
    title: "Beamforming & Phased Arrays: Steering, Nulling & Tracking",
    summary:
      "Book 2 of the bootcamp: electronic beam steering and adaptive arrays, built on the ADAR1000 beamformer and validated against measured antenna patterns.",
    isNew: true,
    tags: ["Beamforming", "PhasedArrays", "MVDR", "NullSteering", "STAP", "Monopulse"],
    images: [
      "/images/projects/radar-beamforming/fig-1.png",
      "/images/projects/radar-beamforming/fig-2.png",
    ],
    links: {
      notion:
        "https://app.notion.com/p/Beamforming-and-Phased-Arrays-37f8349b0e16800fa8cdf58bedb0b502?source=copy_link",
    },
    body: `Book 2 of the RBC Labs bootcamp covers electronic beam steering and adaptive arrays.

Topics: the array factor and beamwidth; sidelobe tapering; grating lobes (spatial aliasing); #NullSteering against jammers (EVM cut from 41% to 5%); #MVDR adaptive beamforming on a 16-channel digital array; space-time adaptive processing (#STAP); synthetic aperture (SAR); and #Monopulse tracking.

Built on the ADAR1000 beamformer and validated against measured antenna patterns.

Builds on #FMCW from Sensing Fundamentals · part of #Radar`,
  },

  // ───────────── Underwater Comms ─────────────
  {
    // Rendered by a dedicated page (src/pages/UnderwaterMimoStory.jsx), routed in App.jsx.
    slug: "hybrid-acoustic-mi-mimo",
    block: "underwater-comms",
    title: "Hybrid Acoustic & Magnetic-Induction Distributed MIMO for Robot Swarms",
    summary:
      "Sound is 200,000× too slow to carry a clock, so distributed MIMO underwater never stays coherent. M²I carries the timing, acoustics carries the data: 4–8 dB SNR gain on a USRP N210 testbed.",
    tags: [
      "UnderwaterComms",
      "DistributedMIMO",
      "Acoustics",
      "MagneticInduction",
      "Swarm",
      "Beamforming",
      "TimeSync",
    ],
    images: [
      "/images/projects/underwater-swarms/lab-testbed.jpg",
      "/images/projects/underwater-swarms/fig-3.png",
    ],
    links: {
      paper:
        "https://www.sciencedirect.com/science/article/abs/pii/S1389128620300165",
    },
    body: `Underwater robot swarms face a communication bottleneck: acoustic #DistributedMIMO needs tight synchronization, but sound travels too slowly for real-time clock alignment.

We solve this with a **hybrid system**: Metamaterial-Enhanced Magnetic Induction (M²I) provides near-instantaneous sync, while acoustic MIMO carries long-range data.

Our SDAMM testbed on USRP N210 demonstrated a **4–8 dB SNR improvement** over pure acoustic sync.

Tags: #UnderwaterComms #Acoustics #MagneticInduction #Swarm`,
  },
  {
    slug: "semantic-video-transfer",
    block: "underwater-comms",
    title: "Semantic Video Transfer for Low-Bandwidth Underwater Acoustics",
    summary:
      "Convert video to semantic prompts on the AUV, send ~102 KB instead of 2.4 MB, regenerate the scene topside with generative AI. ~1 hour → ~2.6 min on a 5.4 kbps link.",
    tags: ["SemanticComms", "UnderwaterComms", "LowBandwidth", "GenerativeAI", "Acoustics"],
    images: ["/images/projects/semantic-video/fig-1.png"],
    links: {
      notion:
        "https://www.notion.so/Semantic-Video-Transfer-for-Low-Bandwidth-Underwater-Acoustics-3288349b0e1680618269f7abad2a4c9e?source=copy_link",
      github:
        "https://github.com/soham12112/Semantic_Underwater_communication_Receiver",
    },
    body: `Acoustic links underwater are limited to under 35 kbps, so raw video transfer takes hours.

Our system converts video into **semantic text prompts** on the AUV, transmits a ~102 KB payload (text + first frame) instead of 2.4 MB of compressed video, and regenerates the scene using generative AI (#GenerativeAI, MiniMax) on the surface.

Transmission drops from **~1 hour to ~2.6 minutes** on a 5.4 kbps link, enabling real-time situational awareness for ocean exploration.

Tags: #SemanticComms #UnderwaterComms #LowBandwidth #Acoustics`,
  },

  // ───────────── Satellite Comms ─────────────
  {
    // Rendered by a dedicated page (src/pages/NrNtnTestbedStory.jsx), routed in App.jsx.
    slug: "nr-ntn-testbed",
    block: "satellite-comms",
    title: "An End-to-End 5G NR NTN Testbed on One Laptop",
    summary:
      "A real OCUDU gNB, a real OAI UE and a real 5G core talking through a LEO satellite channel built from orbit data in GNU Radio. No satellite, no SDR, no MATLAB in the loop. One geometry generates SIB19, the UE position and the channel delay together, so they cannot drift apart.",
    isNew: true,
    added: "2026-09-19",
    tags: [
      "NTN",
      "5GNR",
      "LEO",
      "OCUDU",
      "OpenAirInterface",
      "GNURadio",
      "ChannelModel",
      "Doppler",
      "SatelliteComms",
    ],
    body: `Testing a non-terrestrial feature usually means a satellite, a five-figure channel emulator, or a MATLAB licence in the loop. This rig needs none of them: a standards-compliant #5GNR base station, a real UE and a real 5G core, talking to each other through a #LEO satellite channel built from actual orbit data on one Ubuntu box.

The stack is an #OCUDU gNB (the Linux Foundation open-source CU/DU) on band n256 broadcasting SIB19, Open5GS as the core, an #OpenAirInterface \`nr-uesoftmodem\` on the Release 17 SIB19 path, and a bidirectional #GNURadio channel applying delay, #Doppler, C/N and fading. A bridge translates between OCUDU's ZeroMQ radio and OAI's rfsimulator and owns the timing budget between them.

The idea that makes it work is that **nothing is hand-written**. One geometry, a LEO state vector or a GEO longitude, generates SIB19, the UE position config and the channel delay in a single run, so the delay the channel applies and the timing advance the UE derives cannot drift apart. A consistency test re-derives the advance exactly as OAI does and holds the two to 3 ns.

Tags: #NTN #SatelliteComms #ChannelModel`,
  },
  {
    // Rendered by a dedicated page (src/pages/ThreeGpp.jsx), routed in App.jsx.
    slug: "3gpp-standards-work",
    block: "satellite-comms",
    title: "3GPP Contributions to IoT-NTN",
    summary:
      "Tdocs written and defended in RAN2, RAN4, CT1 and SA as a 3GPP delegate for Skylo, while those groups assembled the IoT-NTN specifications: voice over Non-IP, diversity slotted ALOHA, HPUE power classes and emergency prioritisation.",
    images: ["/images/3gpp/ran-orlando-2024-solo.jpg"],
    tags: ["3GPP", "NTN", "NBIoT", "Standards", "RAN2", "RAN4", "Rel19", "SatelliteComms"],
    body: `#3GPP writes the specifications every cellular device implements, through technical documents submitted to working-group meetings: someone writes a proposal, everyone else tries to break it, and the survivors become normative text.

The unusual part was arguing from a network that was already live. Most contributions to a satellite work item are arguments about simulation; ours were frequently arguments about what had happened, because Skylo was already operating a commercial Rel-17 #NBIoT #NTN network.

Threads I carried included voice over Non-IP Data Delivery, contention-based Msg3 early data transmission (diversity slotted ALOHA) in #Rel19, multiplexed Msg4, high-power UE classes in #RAN4, and emergency traffic prioritisation via reserved PRACH partitions.

Tags: #Standards #SatelliteComms`,
  },
  {
    slug: "nb-ntn-geo-voice",
    block: "satellite-comms",
    title: "First Voice Call over NB-NTN GEO Satellites (Skylo)",
    summary:
      "Skylo's VoNIDD replaces SIP with the I1 protocol, uses Non-IP Data Delivery, and AI codecs to hit MOS 3.9 at under 1 kbps over GEO satellite.",
    tags: ["NTN", "GEO", "VoiceOverNIDD", "AICodecs", "Emergency"],
    images: [
      "/images/projects/ntn-voice/fig-1.png",
      "/images/projects/ntn-voice/fig-2.png",
    ],
    links: {
      notion:
        "https://www.notion.so/First-Voice-Call-over-NB-NTN-GEO-Satellites-3288349b0e168008b21cf4cc9bdbfcfd?source=copy_link",
    },
    body: `Standard SIP-over-IP voice over narrowband satellite links suffers from massive overhead and a 10-transaction call setup.

Skylo's **VoNIDD** architecture replaces SIP with the I1 protocol (setup down to 3 transactions), uses Non-IP Data Delivery (#VoiceOverNIDD) for minimal headers, and employs #AICodecs achieving high voice quality (**MOS 3.9 at under 1 kbps**).

This enables real-time bi-directional voice and #Emergency calling over #GEO satellite infrastructure.

Part of #NTN`,
  },

  // ───────────── LEO PNT ─────────────
  {
    // Rendered by a dedicated page (src/pages/LeoPntStory.jsx), routed in App.jsx.
    slug: "differential-doppler-leo",
    block: "leo-pnt",
    title: "LEO Satellites for Positioning (PNT)",
    summary:
      "Doppler-based snapshot differential positioning over Iridium LEO signals in 5–10 s bursts. SF Bay field tests cut error ~47% (497 m → 265 m). Accepted at IEEE RadarConf 2026.",
    tags: ["GNSS", "PNT", "LEO", "Doppler", "SnapshotPositioning", "SDR", "WildlifeTracking"],
    images: [
      "/images/projects/leo-pnt/fig-3.png",
      "/images/projects/leo-pnt/fig-4.png",
    ],
    links: {
      paper: "https://arxiv.org/abs/2603.15908",
      github: "https://github.com/soham12112/differential_doppler_iridium",
    },
    body: `Traditional #GNSS fails when a receiver can only capture signals for 3–5 seconds, e.g. marine mammals surfacing briefly.

We developed a **Doppler-based snapshot differential positioning** framework using #LEO (Iridium) satellite signals in 5–10 second bursts, with a fixed base station providing differential corrections.

Field experiments in the SF Bay Area with a 30 km baseline reduced positioning error by **~47%**, from 500 m to under 265 m. Paper accepted at **IEEE Radar Conference 2026**.

Tags: #PNT #Doppler #LEO #WildlifeTracking`,
  },

  // ───────────── GNSS & Positioning ─────────────
  {
    slug: "ublox-fast-gnss-sync",
    block: "gnss",
    title: "Using u-blox GNSS Modules for Fast GNSS Sync",
    summary:
      "Using u-blox GNSS modules, their pulse-per-second timing output and assisted-GNSS fixes, to get fast, tightly synchronized time and frequency across distributed receivers.",
    tags: ["GNSS", "Ublox", "TimeSync", "PPS", "PNT"],
    links: {
      notion:
        "https://app.notion.com/p/Using-Ublox-GNSS-Modules-for-Fast-GNSS-Sync-3808349b0e1680e1a726d9c4164171c4?source=copy_link",
    },
    body: `Distributed positioning and #DistributedMIMO experiments need every node to agree on time, and to agree quickly. Off-the-shelf u-blox GNSS modules turn out to be a cheap, capable way to do exactly that.

This note covers using u-blox receivers for **fast GNSS sync**: cutting time-to-first-fix with assisted-GNSS (ephemeris/almanac priors), and disciplining each node's clock to the module's **pulse-per-second (#PPS)** output so that timing and frequency line up across a network of receivers.

The result is a low-cost reference that base stations and roving nodes can share, the timing backbone behind the differential #LEO #PNT work.

Tags: #GNSS #Ublox #TimeSync`,
  },
  {
    slug: "gnss-imu-integration",
    block: "gnss",
    title: "GNSS–IMU Integration for Tracking Motion",
    summary:
      "Fusing GNSS with a low-cost IMU so motion tracking stays continuous, inertial dead reckoning bridges GNSS dropouts and smooths the trajectory.",
    tags: ["GNSS", "IMU", "SensorFusion", "KalmanFilter", "MotionTracking", "PNT"],
    links: {
      notion:
        "https://app.notion.com/p/GNSS-IMU-Integration-for-Tracking-motion-3808349b0e1680ca8fe5c5317067245e?source=copy_link",
    },
    body: `GNSS alone gives absolute position but drops out under cover, indoors, or underwater, and updates too slowly to capture fast dynamics. An IMU gives high-rate motion but drifts. Fusing them gives the best of both.

This note walks through **GNSS–IMU integration**: combining GNSS fixes with accelerometer and gyroscope data through a #KalmanFilter so the estimate stays continuous. Between fixes, inertial **dead reckoning** carries the trajectory; each new GNSS fix corrects the accumulated drift.

The same #SensorFusion idea underpins the marine-sensing work, where kinematics fill in where positioning signals can't reach.

Tags: #GNSS #IMU #MotionTracking #PNT`,
  },

  // ───────────── Whales & Marine Sensing ─────────────
  {
    // Rendered by a dedicated page (src/pages/WhaleGnssTracking.jsx), routed in App.jsx.
    slug: "whale-gnss-dead-reckoning",
    block: "whales",
    title: "Whale Tags: GNSS Positioning & Dead Reckoning",
    summary:
      "A whale surfaces for seconds and dives for an hour, so classical GNSS positions only 1.8% of surfacings. Snapshot code-phase + Doppler lift that 11×; depth/IMU/camera dead reckoning fills the rest, 6.9 m median between samples.",
    isNew: true,
    added: "2026-08-03",
    tags: [
      "GNSS",
      "SnapshotPositioning",
      "Doppler",
      "DeadReckoning",
      "SensorFusion",
      "KalmanFilter",
      "Whales",
      "MarineSensing",
      "WildlifeTracking",
    ],
    images: [
      "/images/projects/whale-gnss/whale_tag.jpg",
      "/images/projects/whale-gnss/fig2_track_overview_both.png",
    ],
    body: `A CATS biologging tag on a blue whale (T4, 23 h) and a humpback (T10, 49 h) in Monterey Bay, with the full narrative, figures and animations on the project page.

The tag is at the surface for 1–10 seconds at a time. Out of 446 and 443 detected surfacings, accepted differential snapshots positioned **7** and **8** of them. The bottleneck was never accuracy, it was the number of usable fixes.

Two GNSS observables were pushed as far as the data allowed, #Doppler arcs through a satellite pass and coarse-time **snapshot code phase** (#SnapshotPositioning), then fused by inverse variance. That lifts the directly-positioned count 11×, from 8 to 90 on the humpback.

The rest is **dead reckoning** (#DeadReckoning): tag jiggle gives speed through water, accel + magnetometer give attitude, pressure observes depth continuously, and a causal #KalmanFilter carries the position between surfacings with each GNSS fix as a measurement update. Coverage goes from 1.8% to ~100% of surfacings, with a measured **6.9 m median** held-out error in the dense 17-second-cadence regime.

Tags: #GNSS #SensorFusion #Whales #MarineSensing #WildlifeTracking`,
  },
];

/* ───────────────────────── helpers ───────────────────────── */

export const getBlock = (slug) => blocks.find((b) => b.slug === slug);

export const notesInBlock = (slug) => notes.filter((n) => n.block === slug);

export const getNote = (blockSlug, noteSlug) =>
  notes.find((n) => n.block === blockSlug && n.slug === noteSlug);

export const notesWithTag = (tag) =>
  notes.filter((n) => n.tags?.some((t) => t.toLowerCase() === tag.toLowerCase()));

/** Sorted [tag, count] pairs across all notes. */
export const tagCounts = () => {
  const counts = {};
  for (const n of notes) for (const t of n.tags || []) counts[t] = (counts[t] || 0) + 1;
  return Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
};

export const blockStats = (slug) => {
  const items = notesInBlock(slug);
  return { count: items.length, hasNew: items.some((n) => n.isNew) };
};
